import { css } from '@emotion/css';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { ReactComponent as Balloon } from '../../../assets/images/balloon.svg';
import { BestFriendProfileErrorType } from '../../../features/bestFriends';
import { sendEvent } from '../../../lib/google-analytics';
import { replaceAllSpaces } from '../../../lib/utils';
import { FriendItem } from '../../../models/FriendItem';
import { FancyButton } from '../../shared/buttons/FancyButton';
import { CharacterImage } from '../../shared/CharacterImage';
import { IslandName } from '../../shared/IslandName';
import { ProfileErrorText } from './ProfileErrorText';

interface FriendDetailProps {
  friendItem: FriendItem;
  comment?: string;
  profileErrorType?: BestFriendProfileErrorType;
}

export const FriendDetail: React.FC<FriendDetailProps> = ({
  friendItem,
  comment,
  profileErrorType
}) => {
  const { t, i18n } = useTranslation(['best_friends', 'translation']);
  const history = useHistory();
  const routeMatch = useRouteMatch();
  const location = useLocation();

  const hasMenu = routeMatch?.url === location.pathname;

  const handleMessageClick = () => {
    if (friendItem) {
      const { id } = friendItem;
      history.push(`/players/best-friends-message/${id}`);
      sendEvent('best_friends_person_message_to_person', 'click');
    }
  };

  const { name, image, landName, landDisplayId, isOnline, isReported } = friendItem;

  return (
    <div
      className={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        color: #938666;

        .player-image {
          width: 88px;
          height: 88px;
          margin: 0 auto 12px;
          position: relative;

          &::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 32px;
            height: 32px;
            border: 6px solid #f5f1e3;
            border-radius: 50%;
            background: ${isOnline ? '#50b163' : '#707070'};
            visibility: ${isReported ? 'hidden' : 'visible'};
            /* workaround for iOS 13.3.1  */
            z-index: ${hasMenu ? 1 : 'inherit'};
            transform: translateZ(0);
          }
        }
        .island {
          font-size: 14px;
        }
        .name {
          color: #493a31;
          margin: 8px 0;

          &.reported {
            padding: 16px 0 48px;
          }
        }
        .comment {
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 14px;
          height: 40px;
          color: #938666;
        }
        footer {
          width: 100%;
          text-align: center;
          margin: 16px 0 8px;
        }
        footer p {
          font-size: 12px;
          color: #9c9c9c;
          margin: 12px 0 0;
        }
        .message {
          display: flex;
          align-items: center;
          padding: 0 4px;
        }
        .message span {
          flex: 1 1 auto;
          padding: 0 40px 0 0;
        }
        @media (min-width: 370px) {
          .player-image {
            width: 120px;
            height: 120px;
          }
          .island {
            font-size: 16px;
          }
          .name {
            font-size: 20px;
          }
        }
      `}
    >
      <div className="player-image">
        <CharacterImage
          src={image}
          isPlayer={true}
          isReported={isReported}
          imageAriaLabel={`${isOnline ? t('online') : t('offline')} ${t('aria_player_image')}`}
          ariaHidden={!hasMenu}
        />
      </div>
      {isReported ? (
        <p className="name reported" aria-hidden={!hasMenu}>
          {t('translation:reported_player')}
        </p>
      ) : (
        <>
          <p className="island" aria-hidden={!hasMenu}>
            <IslandName
              landName={landName}
              landDisplayId={landDisplayId}
              language={i18n.language}
            />
          </p>
          <p className="name" aria-hidden={!hasMenu}>
            {replaceAllSpaces(name)}
          </p>
          <p className="comment" aria-hidden={!hasMenu}>
            {replaceAllSpaces(comment || '')}
          </p>
        </>
      )}
      <footer aria-hidden={!hasMenu}>
        <FancyButton
          background={'#fb984a'}
          color={'white'}
          onClick={handleMessageClick}
          hidden={!hasMenu}
        >
          <div className="message" aria-hidden={!hasMenu} aria-label={t('aria_message_to_person')}>
            <Balloon />
            <span>{t('button_message')}</span>
          </div>
        </FancyButton>
        <p>{t('note_offline')}</p>
        <ProfileErrorText profileErrorType={profileErrorType} />
      </footer>
    </div>
  );
};
